import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Printer, Download, CheckSquare } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* ─── Palette ──────────────────────────────────────── */
const C = {
  cream:     '#D8C9A8',   // medium-dark warm cream — template body
  beige:     '#CABB98',   // deeper beige for contrast panels

  charcoal:  '#1E1A14',   // header band + strong text
  charText:  '#1E1A14',   // body headings — strong on medium cream
  charMid:   '#2E2418',   // body text — strong on medium cream
  charLight: '#4E4030',   // secondary text — legible but softer

  gold:      '#C8A97E',   // primary accent — gold band border
  goldBr:    '#D9BC94',   // lighter gold
  goldDeep:  '#8C6838',   // deep gold — readable on medium cream   // deep gold for arrow / rule

  sage:      '#8A9A8B',   // sage footer bg border
  sageDark:  '#4E6455',   // dark sage text
  sagePale:  '#DDE9DF',   // pale sage footer background
  sageMid:   '#6B8070',   // medium sage

  white:     '#FFFFFF',
};
const F = { serif:'"Playfair Display", Georgia, serif', sans:'Montserrat, Arial, sans-serif' };

/* ─── Structural Components ────────────────────────── */

/* Charcoal header band + thick gold bottom border */
const HeaderBand = ({ h=52, logoSize=15 }) => (
  <div style={{
    height:h, background:C.charcoal, borderBottom:`3.5px solid ${C.gold}`,
    display:'flex', alignItems:'center', justifyContent:'space-between',
    padding:'0 16px', flexShrink:0,
  }}>
    <div style={{display:'flex',alignItems:'center',gap:5}}>
      <span style={{fontFamily:F.serif,fontWeight:800,fontSize:logoSize,color:C.cream,letterSpacing:'-0.01em',lineHeight:1}}>Perkly</span>
      <div style={{width:Math.round(logoSize*0.28),height:Math.round(logoSize*0.28),borderRadius:'50%',background:C.gold,flexShrink:0}}/>
    </div>
    <span style={{fontFamily:F.sans,fontSize:7,color:`${C.gold}75`,letterSpacing:'0.2em',textTransform:'uppercase'}}>Athens, GA</span>
  </div>
);

/* Sage green footer strip — darker sage bg, white text */
const SageFooter = ({ name, url='useperkly.com', h=34, nameSize=8.5 }) => (
  <div style={{
    height:h, background:C.sageDark, borderTop:`2px solid ${C.sageMid}`,
    display:'flex', alignItems:'center', justifyContent:'space-between',
    padding:'0 16px', flexShrink:0,
  }}>
    <div style={{display:'flex',alignItems:'center',gap:6}}>
      <div style={{width:2,height:16,background:C.gold,borderRadius:1,flexShrink:0}}/>
      <div>
        <div style={{fontFamily:F.sans,fontSize:nameSize-2,color:`${C.white}70`,letterSpacing:'0.1em',textTransform:'uppercase',lineHeight:1.1}}>Presented by</div>
        <div style={{fontFamily:F.serif,fontSize:nameSize,fontWeight:700,color:C.white,lineHeight:1.1}}>{name}</div>
      </div>
    </div>
    <span style={{fontFamily:F.sans,fontSize:7,color:`${C.white}60`,letterSpacing:'0.08em'}}>{url}</span>
  </div>
);

/* Gold rule with diamond tip */
const GoldRule = ({ w=44, my=8 }) => (
  <div style={{display:'flex',alignItems:'center',gap:4,margin:`${my}px 0`,flexShrink:0}}>
    <div style={{width:w,height:1.5,background:C.gold,flexShrink:0}}/>
    <div style={{width:5,height:5,background:C.gold,transform:'rotate(45deg)',flexShrink:0}}/>
    <div style={{flex:1,height:1,background:`linear-gradient(to right,${C.gold}50,transparent)`}}/>
  </div>
);

/* QR box — white + gold border */
const QRBox = ({ src, size=90, pad=8 }) => (
  <div style={{
    display:'inline-block', background:C.white,
    border:`2.5px solid ${C.gold}`, borderRadius:8, padding:pad, lineHeight:0,
    boxShadow:`0 5px 20px rgba(160,120,80,0.22), 0 2px 6px rgba(30,26,20,0.08)`,
    position:'relative', flexShrink:0,
  }}>
    {src
      ? <img src={src} alt="QR" style={{width:size,height:size,display:'block',borderRadius:3,background:'#fff'}}/>
      : <div style={{width:size,height:size,background:C.beige,borderRadius:3,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <span style={{fontFamily:F.sans,fontSize:9,fontWeight:600,color:C.goldDeep}}>QR Code</span>
        </div>}
    <div style={{position:'absolute',bottom:-12,left:'50%',transform:'translateX(-50%)',whiteSpace:'nowrap',
      background:C.charcoal,borderRadius:4,padding:'2px 8px',
      fontFamily:F.sans,fontSize:7,fontWeight:800,color:C.gold,letterSpacing:'0.14em',textTransform:'uppercase'}}>
      SCAN ME
    </div>
  </div>
);

const ScanText = ({ size=9 }) => (
  <div style={{lineHeight:1.65}}>
    <div style={{display:'flex',alignItems:'center',gap:4}}>
      <span style={{fontFamily:F.sans,fontSize:size,color:C.charMid}}>Open your camera</span>
      <span style={{color:C.goldDeep,fontSize:size+2,lineHeight:1,fontWeight:700}}>→</span>
    </div>
    <span style={{fontFamily:F.sans,fontSize:size,color:C.charMid}}>Scan to discover deals</span>
  </div>
);

const CatPill = ({ icon, label }) => (
  <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
    <div style={{width:30,height:30,borderRadius:7,background:C.sagePale,border:`1.5px solid ${C.sage}50`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14}}>
      {icon}
    </div>
    <span style={{fontFamily:F.sans,fontSize:6.5,fontWeight:700,letterSpacing:'0.1em',textTransform:'uppercase',color:C.sageDark}}>{label}</span>
  </div>
);

/* Template wrapper — flex column so header/body/footer stack cleanly */
const T = ({ w, h, children, bg=C.cream, style={} }) => (
  <div style={{width:w,height:h,background:bg,borderRadius:10,overflow:'hidden',position:'relative',fontFamily:F.sans,flexShrink:0,display:'flex',flexDirection:'column',...style}}>
    {children}
  </div>
);

/* ─── A4 POSTER ─────────────────────────────────────── */
function A4Poster({ biz, qr }) {
  return (
    <T w={370} h={564}>
      <HeaderBand h={54} logoSize={16}/>
      <div style={{flex:1,padding:'18px 26px 10px',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <GoldRule w={44} my={0}/>
        <div style={{marginTop:12,marginBottom:8}}>
          <h1 style={{fontFamily:F.serif,fontSize:50,fontWeight:800,color:C.charText,margin:0,lineHeight:0.96,letterSpacing:'-0.02em'}}>Discover</h1>
          <h1 style={{fontFamily:F.serif,fontSize:50,fontWeight:800,color:C.charText,margin:0,lineHeight:0.96,letterSpacing:'-0.02em'}}>Athens.</h1>
          <h2 style={{fontFamily:F.serif,fontSize:34,fontWeight:700,color:C.goldDeep,fontStyle:'italic',margin:'6px 0 0',lineHeight:1.1}}>Save Money.</h2>
        </div>
        <p style={{fontFamily:F.sans,fontSize:8.5,color:C.charLight,margin:'8px 0 14px',lineHeight:1.7,letterSpacing:'0.06em'}}>
          EXCLUSIVE DEALS · LOCAL BUSINESSES · ATHENS STUDENTS
        </p>
        <div style={{display:'flex',gap:14,marginBottom:18}}>
          {[['🍔','Food'],['☕','Coffee'],['⭐','Events'],['🏷️','Deals']].map(([ic,lb])=>(
            <CatPill key={lb} icon={ic} label={lb}/>
          ))}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:22,flex:1}}>
          <div style={{flex:1}}>
            <ScanText size={10}/>
            <div style={{marginTop:10,width:40,height:1.5,background:`${C.gold}60`}}/>
            <p style={{fontFamily:F.sans,fontSize:8.5,color:C.charLight,margin:'8px 0 0',lineHeight:1.6}}>
              Free to use.<br/>Student verified.
            </p>
          </div>
          <QRBox src={qr} size={110}/>
        </div>
      </div>
      <SageFooter name={biz} h={36}/>
    </T>
  );
}

/* ─── WINDOW STICKER ─────────────────────────────────── */
function WindowSticker({ biz, qr }) {
  return (
    <T w={220} h={245}>
      <HeaderBand h={46} logoSize={14}/>
      <div style={{flex:1,padding:'12px 15px 8px',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <GoldRule w={28} my={0}/>
        <h2 style={{fontFamily:F.serif,fontSize:21,fontWeight:800,color:C.charText,margin:'8px 0 1px',lineHeight:1.05}}>Discover Athens.</h2>
        <h3 style={{fontFamily:F.serif,fontSize:14,fontWeight:700,color:C.goldDeep,fontStyle:'italic',margin:'0 0 8px',lineHeight:1.1}}>Save Money.</h3>
        <div style={{display:'flex',alignItems:'flex-end',gap:10,flex:1}}>
          <div style={{flex:1,paddingBottom:4}}><ScanText size={8}/></div>
          <QRBox src={qr} size={70}/>
        </div>
      </div>
      <SageFooter name={biz} h={30} nameSize={7.5}/>
    </T>
  );
}

/* ─── DOOR STICKER ─────────────────────────────────── */
function DoorSticker({ biz, qr }) {
  return (
    <T w={220} h={360}>
      <HeaderBand h={48} logoSize={14}/>
      <div style={{flex:1,padding:'15px 18px 8px',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <GoldRule w={32} my={0}/>
        <h2 style={{fontFamily:F.serif,fontSize:27,fontWeight:800,color:C.charText,margin:'10px 0 3px',lineHeight:1.05}}>Discover<br/>Athens.</h2>
        <h3 style={{fontFamily:F.serif,fontSize:17,fontWeight:700,color:C.goldDeep,fontStyle:'italic',margin:'0 0 8px',lineHeight:1.1}}>Save Money.</h3>
        <p style={{fontFamily:F.sans,fontSize:8.5,color:C.charLight,margin:'0 0 14px',lineHeight:1.6,letterSpacing:'0.04em'}}>EXCLUSIVE DEALS · LOCAL BUSINESSES</p>
        <div style={{marginBottom:16}}><ScanText size={9}/></div>
        <QRBox src={qr} size={94}/>
        <div style={{flex:1}}/>
      </div>
      <SageFooter name={biz} h={32} nameSize={8}/>
    </T>
  );
}

/* ─── TABLE TENT FRONT ─────────────────────────────── */
function TableTentFront({ biz, qr }) {
  return (
    <T w={220} h={310} style={{borderRadius:'10px 10px 0 0'}}>
      <HeaderBand h={46} logoSize={14}/>
      <div style={{flex:1,padding:'13px 16px 8px',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <GoldRule w={28} my={0}/>
        <h2 style={{fontFamily:F.serif,fontSize:23,fontWeight:800,color:C.charText,margin:'9px 0 2px',lineHeight:1.05}}>Discover<br/>Athens.</h2>
        <h3 style={{fontFamily:F.serif,fontSize:15,fontWeight:700,color:C.goldDeep,fontStyle:'italic',margin:'0 0 8px',lineHeight:1.1}}>Save Money.</h3>
        <p style={{fontFamily:F.sans,fontSize:8,color:C.charLight,margin:'0 0 12px',lineHeight:1.5}}>Exclusive deals from local businesses.</p>
        <QRBox src={qr} size={84}/>
        <div style={{marginTop:12}}><ScanText size={8}/></div>
      </div>
      <SageFooter name={biz} h={30} nameSize={7.5} url=""/>
    </T>
  );
}

/* ─── TABLE TENT BACK ──────────────────────────────── */
function TableTentBack({ biz }) {
  const steps = [
    {n:'01',title:'Scan',desc:'Scan the QR code with your phone camera.'},
    {n:'02',title:'Claim',desc:'Browse deals and claim the one you love.'},
    {n:'03',title:'Show',desc:'Show the code at checkout.'},
    {n:'04',title:'Save',desc:'Enjoy exclusive savings today!'},
  ];
  return (
    <T w={220} h={290} bg={C.beige} style={{borderRadius:'0 0 10px 10px'}}>
      {/* Top sage stripe */}
      <div style={{height:3,background:`linear-gradient(to right,${C.beige},${C.sage}80,${C.beige})`,flexShrink:0}}/>
      <div style={{flex:1,padding:'14px 16px',display:'flex',flexDirection:'column'}}>
        {/* HOW IT WORKS header */}
        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:12}}>
          <div style={{flex:1,height:1.5,background:`${C.sage}40`}}/>
          <span style={{fontFamily:F.sans,fontSize:8,fontWeight:800,letterSpacing:'0.2em',textTransform:'uppercase',color:C.sageDark}}>How It Works</span>
          <div style={{flex:1,height:1.5,background:`${C.sage}40`}}/>
        </div>
        {steps.map(s => (
          <div key={s.n} style={{display:'flex',gap:10,marginBottom:11,alignItems:'flex-start'}}>
            <div style={{width:24,height:24,borderRadius:7,background:C.sagePale,border:`1.5px solid ${C.sage}50`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
              <span style={{fontFamily:F.sans,fontSize:7.5,fontWeight:800,color:C.sageDark,lineHeight:1}}>{s.n}</span>
            </div>
            <div>
              <div style={{fontFamily:F.serif,fontSize:12,fontWeight:700,color:C.charText,marginBottom:1}}>{s.title}</div>
              <div style={{fontFamily:F.sans,fontSize:8,color:C.charMid,lineHeight:1.45}}>{s.desc}</div>
            </div>
          </div>
        ))}
        <div style={{flex:1}}/>
        <div style={{borderTop:`1px solid ${C.gold}35`,paddingTop:8,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <span style={{fontFamily:F.sans,fontSize:8,color:C.charLight}}>Thank you for supporting local</span>
          <span style={{color:C.gold,fontSize:12}}>♥</span>
        </div>
      </div>
      {/* Bottom sage strip */}
      <div style={{height:3,background:`linear-gradient(to right,${C.beige},${C.sage}80,${C.beige})`,flexShrink:0}}/>
    </T>
  );
}

/* ─── MENU INSERT ──────────────────────────────────── */
function MenuInsert({ biz, qr }) {
  return (
    <T w={160} h={258}>
      <HeaderBand h={44} logoSize={13}/>
      <div style={{flex:1,padding:'11px 13px 8px',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <GoldRule w={24} my={0}/>
        <h3 style={{fontFamily:F.serif,fontSize:15,fontWeight:800,color:C.charText,margin:'9px 0 3px',lineHeight:1.15}}>Looking for<br/>today&apos;s deals?</h3>
        <p style={{fontFamily:F.sans,fontSize:8,color:C.sageDark,fontWeight:600,margin:'0 0 12px',lineHeight:1.5}}>Open your camera<br/>and scan below.</p>
        <QRBox src={qr} size={74}/>
        <div style={{flex:1}}/>
      </div>
      <SageFooter name={biz} h={28} nameSize={7} url=""/>
    </T>
  );
}

/* ─── COUNTER CARD ──────────────────────────────────── */
function CounterCard({ biz, qr }) {
  return (
    <T w={340} h={200}>
      {/* Full-width charcoal header */}
      <HeaderBand h={44} logoSize={14}/>
      {/* Body: left content + right QR */}
      <div style={{flex:1,display:'flex',overflow:'hidden'}}>
        {/* Left */}
        <div style={{flex:1,padding:'14px 18px',display:'flex',flexDirection:'column',justifyContent:'space-between'}}>
          <div>
            <GoldRule w={32} my={0}/>
            <h2 style={{fontFamily:F.serif,fontSize:19,fontWeight:800,color:C.charText,margin:'8px 0 3px',lineHeight:1.15}}>Exclusive deals<br/>while you wait.</h2>
            <p style={{fontFamily:F.sans,fontSize:8,color:C.charMid,margin:0,lineHeight:1.5}}>Scan to unlock student savings.</p>
          </div>
          <ScanText size={7.5}/>
        </div>
        {/* Divider */}
        <div style={{width:1,background:`linear-gradient(to bottom,${C.cream},${C.gold}50,${C.cream})`,margin:'10px 0',flexShrink:0}}/>
        {/* Right QR panel — slightly deeper cream */}
        <div style={{width:136,background:C.beige,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:10,padding:'10px'}}>
          <QRBox src={qr} size={84}/>
        </div>
      </div>
      {/* Full-width sage footer */}
      <SageFooter name={biz} h={32} nameSize={7.5}/>
    </T>
  );
}

/* ─── RECEIPT STICKER ──────────────────────────────── */
function ReceiptSticker({ biz, qr }) {
  return (
    <T w={220} h={220}>
      <HeaderBand h={44} logoSize={14}/>
      <div style={{flex:1,padding:'12px 16px 8px',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <GoldRule w={30} my={0}/>
        <div style={{display:'flex',alignItems:'flex-start',gap:12,flex:1,marginTop:8}}>
          <div style={{flex:1}}>
            <p style={{fontFamily:F.serif,fontSize:14,fontWeight:700,color:C.charText,margin:'0 0 4px',lineHeight:1.25}}>Thanks for<br/>supporting local!</p>
            <p style={{fontFamily:F.sans,fontSize:8,color:C.sageDark,fontWeight:600,margin:'0 0 8px',lineHeight:1.5}}>Scan for exclusive<br/>deals today.</p>
            <ScanText size={7.5}/>
          </div>
          <QRBox src={qr} size={70}/>
        </div>
      </div>
      <SageFooter name={biz} h={30} nameSize={7.5}/>
    </T>
  );
}

/* ─── SOCIAL MEDIA ─────────────────────────────────── */
function SocialTemplate({ biz, qr }) {
  return (
    <T w={300} h={316}>
      <HeaderBand h={50} logoSize={15}/>
      <div style={{flex:1,padding:'14px 18px 8px',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <GoldRule w={36} my={0}/>
        <div style={{marginTop:10,marginBottom:8}}>
          <h2 style={{fontFamily:F.serif,fontSize:29,fontWeight:800,color:C.charText,margin:0,lineHeight:1.0,letterSpacing:'-0.02em'}}>Discover Athens.</h2>
          <h3 style={{fontFamily:F.serif,fontSize:20,fontWeight:700,color:C.goldDeep,fontStyle:'italic',margin:'4px 0 5px',lineHeight:1.1}}>Save Money.</h3>
          <p style={{fontFamily:F.sans,fontSize:8,color:C.charLight,margin:0,letterSpacing:'0.07em',textTransform:'uppercase'}}>Exclusive local deals · Student verified</p>
        </div>
        <div style={{display:'flex',gap:10,marginBottom:14}}>
          {[['🍔','Food'],['☕','Coffee'],['⭐','Events'],['🏷️','Deals']].map(([ic,lb])=>(
            <CatPill key={lb} icon={ic} label={lb}/>
          ))}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:16,flex:1}}>
          <div style={{flex:1}}><ScanText size={8.5}/></div>
          <QRBox src={qr} size={84}/>
        </div>
      </div>
      <SageFooter name={biz} h={34}/>
    </T>
  );
}

/* ─── Template config ───────────────────────────────── */
const ALL_TEMPLATES = [
  { id:'a4-poster',       label:'A4 Poster',            size:'8.3" × 11.7"' },
  { id:'counter-card',    label:'Counter Card',          size:'5" × 3"' },
  { id:'window-sticker',  label:'Window Sticker',        size:'4" × 4"' },
  { id:'door-sticker',    label:'Door Sticker',          size:'4" × 6"' },
  { id:'table-tent',      label:'Table Tent',            size:'4" × 6" folded' },
  { id:'menu-insert',     label:'Menu QR Insert',        size:'2" × 3"' },
  { id:'receipt-sticker', label:'Receipt Sticker',       size:'3" × 3"' },
  { id:'social-media',    label:'Social Media (1080²)',  size:'1080 × 1080px' },
];

const GOOGLE_FONTS_URL = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=Montserrat:wght@400;500;600;700&display=swap";

/* ─── Main Page ─────────────────────────────────────── */
export default function MarketingKitPage() {
  const [searchParams] = useSearchParams();
  const slug = searchParams.get('slug') || '';
  const [ref, setRef]     = useState(null);
  const [bizName, setBizName] = useState('Your Business');
  const [selected, setSelected] = useState(new Set(ALL_TEMPLATES.map(t => t.id)));
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/businesses/referral`, { credentials:'include' })
      .then(r => r.ok ? r.json() : null).then(d => { if (d) setRef(d); }).catch(() => {});
    if (slug) {
      fetch(`${API}/r/${slug}`)
        .then(r => r.ok ? r.json() : null).then(d => { if (d) setBizName(d.business_name); }).catch(() => {});
    }
  }, [slug]);

  const qr = ref?.qr_base64 || '';
  const p  = { biz: bizName, qr };

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const selectAll   = () => setSelected(new Set(ALL_TEMPLATES.map(t => t.id)));
  const deselectAll = () => setSelected(new Set());

  /* ── Print popup ── */
  const openPrintWindow = (ids) => {
    if (ids.length === 0) { setError('Please select at least one marketing material.'); return; }
    setError('');

    const getTemplateHTML = (id) => {
      const el = document.getElementById(`tmpl-${id}`);
      if (!el) return '';
      const clone = el.cloneNode(true);
      clone.querySelectorAll('img[src^="data:"]').forEach(img => {
        const w = img.offsetWidth  || parseInt(img.style.width)  || 90;
        const h = img.offsetHeight || parseInt(img.style.height) || 90;
        img.setAttribute('width', w); img.setAttribute('height', h);
        img.style.width = w+'px'; img.style.height = h+'px';
      });
      return clone.outerHTML;
    };

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Perkly Marketing Kit — ${bizName}</title>
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="${GOOGLE_FONTS_URL}" rel="stylesheet">
  <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    html,body{background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
    @page{size:A4 portrait;margin:8mm;}
    .page{page-break-after:always;break-after:page;width:100%;height:100vh;display:flex;align-items:center;justify-content:center;padding:4mm;box-sizing:border-box;overflow:hidden;}
    .page:last-child{page-break-after:auto;break-after:auto;}
    .page-fit{display:inline-flex;transform-origin:center center;}
    @media print{.no-print{display:none!important;}}
  </style>
</head>
<body>
  <div class="no-print" style="padding:16px;text-align:center;font-family:sans-serif;background:#FAF7F0;">
    <p style="margin-bottom:12px;color:#5A5040;font-size:14px;font-family:'Georgia',serif;">Perkly Marketing Kit — ${bizName}</p>
    <button onclick="window.print()" style="background:#1E1A14;color:#C8A97E;border:none;padding:10px 24px;border-radius:20px;cursor:pointer;font-size:14px;font-weight:700;letter-spacing:0.05em;">Print / Save as PDF</button>
    <hr style="margin:16px 0;border-color:#DDE9DF;">
  </div>
  ${ids.map(id => {
      const tmplHtml = getTemplateHTML(id);
      return tmplHtml ? `<div class="page"><div class="page-fit">${tmplHtml}</div></div>` : '';
    }).filter(Boolean).join('\n')}
  <script>
    window.addEventListener('load', function() {
      var imgs = document.querySelectorAll('img');
      var promises = Array.from(imgs).map(function(img) {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise(function(resolve) { img.onload = resolve; img.onerror = resolve; });
      });
      Promise.all(promises).then(function() {
        document.querySelectorAll('.page-fit').forEach(function(el) {
          var page = el.closest('.page');
          var aw = page.clientWidth - 12, ah = page.clientHeight - 12;
          var ew = el.scrollWidth || el.offsetWidth, eh = el.scrollHeight || el.offsetHeight;
          if (ew > 0 && eh > 0) {
            var scale = Math.min(aw/ew, ah/eh, 1);
            if (scale < 1) el.style.transform = 'scale(' + scale + ')';
          }
        });
        setTimeout(function() { window.print(); }, 500);
      });
    });
  </script>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const blobUrl = URL.createObjectURL(blob);
    const pw = window.open(blobUrl, '_blank', 'width=900,height=700,scrollbars=yes');
    if (!pw) { URL.revokeObjectURL(blobUrl); setError('Pop-up blocked. Please allow pop-ups for this site.'); return; }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  };

  /* ── Download PDF ── */
  const downloadPDF = async (ids) => {
    if (ids.length === 0) { setError('Please select at least one material.'); return; }
    setError('');
    setGenerating(true);
    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'), import('html2canvas'),
      ]);
      const pdf = new jsPDF({ orientation:'portrait', unit:'mm', format:'a4' });
      const A4_W = 210, A4_H = 297;
      let firstPage = true;

      for (const id of ids) {
        const el = document.getElementById(`tmpl-${id}`);
        if (!el) continue;
        const imgs = Array.from(el.querySelectorAll('img'));
        await Promise.all(imgs.map(img => {
          if (img.complete && img.naturalWidth > 0)
            return img.decode ? img.decode().catch(() => {}) : Promise.resolve();
          return new Promise(res => { img.onload = res; img.onerror = res; });
        }));
        const canvas = await html2canvas(el, {
          scale:2.5, useCORS:true, allowTaint:true,
          backgroundColor: C.cream, logging:false, imageTimeout:15000,
          onclone: (_doc, clonedEl) => {
            clonedEl.querySelectorAll('img[src^="data:"]').forEach(img => {
              const nW = img.naturalWidth  || parseInt(img.style.width)  || 90;
              const nH = img.naturalHeight || parseInt(img.style.height) || 90;
              const dW = parseInt(img.style.width)  || 90;
              const dH = parseInt(img.style.height) || 90;
              const cvs = _doc.createElement('canvas');
              cvs.width=nW; cvs.height=nH;
              cvs.style.width=dW+'px'; cvs.style.height=dH+'px';
              cvs.style.display='block'; cvs.style.borderRadius=img.style.borderRadius||'3px';
              cvs.getContext('2d').drawImage(img,0,0,nW,nH);
              img.parentNode?.replaceChild(cvs,img);
            });
          },
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        const aspect  = canvas.width / canvas.height;
        let imgW = A4_W-20, imgH = imgW/aspect;
        if (imgH > A4_H-20) { imgH=A4_H-20; imgW=imgH*aspect; }
        if (!firstPage) pdf.addPage();
        pdf.addImage(imgData,'JPEG',(A4_W-imgW)/2,(A4_H-imgH)/2,imgW,imgH);
        firstPage = false;
      }
      pdf.save(`perkly-marketing-kit-${bizName.toLowerCase().replace(/\s+/g,'-')}.pdf`);
    } catch (err) {
      setError('Could not generate PDF. Please try again or use the Print option.');
      console.error(err);
    }
    setGenerating(false);
  };

  const selectedIds = [...selected];
  const allSelected = selected.size === ALL_TEMPLATES.length;

  return (
    <>
      <style>{`@import url('${GOOGLE_FONTS_URL}');@media print{.kit-no-print{display:none!important;}body{background:white!important;}}`}</style>

      {/* Page Header */}
      <div className="kit-no-print" data-testid="marketing-kit-header" style={{
        background:C.charcoal, padding:'13px 24px', borderBottom:`3.5px solid ${C.gold}`,
        display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12,
      }}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontFamily:F.serif,fontWeight:800,fontSize:18,color:C.cream,letterSpacing:'-0.01em'}}>Perkly</span>
          <div style={{width:6,height:6,borderRadius:'50%',background:C.gold}}/>
          <span style={{fontFamily:F.sans,fontSize:11,color:`${C.cream}50`,letterSpacing:'0.06em'}}>Marketing Kit · {bizName}</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
          <button data-testid="print-selected-btn"
            onClick={() => openPrintWindow(selectedIds)} disabled={generating}
            style={{display:'flex',alignItems:'center',gap:6,background:C.gold,color:C.charcoal,border:'none',borderRadius:20,padding:'8px 18px',fontFamily:F.sans,fontWeight:700,fontSize:12,cursor:'pointer'}}>
            <Printer size={13}/> Print Selected
          </button>
          <button data-testid="download-pdf-btn"
            onClick={() => downloadPDF(selectedIds)} disabled={generating}
            style={{display:'flex',alignItems:'center',gap:6,background:C.cream,color:C.charcoal,border:'none',borderRadius:20,padding:'8px 18px',fontFamily:F.sans,fontWeight:700,fontSize:12,cursor:'pointer'}}>
            <Download size={13}/> {generating ? 'Generating…' : 'Download PDF'}
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="kit-no-print" data-testid="marketing-kit-toolbar" style={{
        background:C.cream, borderBottom:`1px solid ${C.gold}45`,
        padding:'11px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12,
      }}>
        <div style={{display:'flex',alignItems:'center',gap:14}}>
          <button data-testid="select-all-btn"
            onClick={allSelected ? deselectAll : selectAll}
            style={{display:'flex',alignItems:'center',gap:6,fontFamily:F.sans,fontSize:12,fontWeight:600,color:C.charText,background:'none',border:`1.5px solid ${C.gold}`,borderRadius:20,padding:'5px 14px',cursor:'pointer'}}>
            <CheckSquare size={13} style={{color:C.gold}}/> {allSelected ? 'Deselect All' : 'Select All'}
          </button>
          <span style={{fontFamily:F.sans,fontSize:12,color:C.charLight}}>
            {selected.size} / {ALL_TEMPLATES.length} selected
          </span>
        </div>
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          <button data-testid="print-all-btn"
            onClick={() => openPrintWindow(ALL_TEMPLATES.map(t=>t.id))}
            style={{display:'flex',alignItems:'center',gap:5,fontFamily:F.sans,fontSize:12,fontWeight:600,color:C.charText,background:C.cream,border:`1.5px solid ${C.gold}50`,borderRadius:20,padding:'5px 14px',cursor:'pointer'}}>
            <Printer size={12}/> Print All
          </button>
          <button data-testid="download-all-pdf-btn"
            onClick={() => downloadPDF(ALL_TEMPLATES.map(t=>t.id))} disabled={generating}
            style={{display:'flex',alignItems:'center',gap:5,fontFamily:F.sans,fontSize:12,fontWeight:600,color:C.charText,background:C.cream,border:`1.5px solid ${C.gold}50`,borderRadius:20,padding:'5px 14px',cursor:'pointer'}}>
            <Download size={12}/> Download All PDF
          </button>
        </div>
      </div>

      {error && (
        <div className="kit-no-print" data-testid="marketing-kit-error"
          style={{background:'#FEF2E8',borderBottom:`1px solid #F6AD55`,padding:'10px 24px',fontFamily:F.sans,fontSize:13,color:'#C05621'}}>
          {error}
        </div>
      )}

      {/* Templates Grid */}
      <div data-testid="templates-grid" style={{
        background:`linear-gradient(160deg,${C.cream} 0%,${C.beige}80 100%)`,
        padding:'28px 24px', display:'flex', flexWrap:'wrap', gap:28,
        alignItems:'flex-start', justifyContent:'center', minHeight:'60vh',
      }}>
        {ALL_TEMPLATES.map(tmpl => (
          <div key={tmpl.id} style={{display:'flex',flexDirection:'column',gap:10,alignItems:'flex-start'}}>
            <label className="kit-no-print" style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',userSelect:'none'}}>
              <div data-testid={`select-tmpl-${tmpl.id}`}
                onClick={() => toggleSelect(tmpl.id)}
                style={{
                  width:18,height:18,borderRadius:4,
                  border:`2px solid ${selected.has(tmpl.id)?C.gold:C.charLight+'50'}`,
                  background:selected.has(tmpl.id)?C.gold:'transparent',
                  display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'all 0.15s',
                }}>
                {selected.has(tmpl.id) && <span style={{color:C.charcoal,fontSize:11,fontWeight:900,lineHeight:1}}>✓</span>}
              </div>
              <div>
                <div style={{fontFamily:F.sans,fontSize:11,fontWeight:700,color:C.charText}}>{tmpl.label}</div>
                <div style={{fontFamily:F.sans,fontSize:9.5,color:C.charLight}}>{tmpl.size}</div>
              </div>
            </label>

            <div data-testid={`tmpl-wrapper-${tmpl.id}`} id={`tmpl-${tmpl.id}`}
              style={{
                opacity:selected.has(tmpl.id)?1:0.45, transition:'opacity 0.2s',
                boxShadow:selected.has(tmpl.id)
                  ?`0 0 0 2px ${C.gold}, 0 8px 32px rgba(30,26,20,0.14), 0 2px 6px rgba(30,26,20,0.06)`
                  :'0 4px 16px rgba(30,26,20,0.08)',
                borderRadius:10,
              }}>
              {tmpl.id === 'a4-poster'       && <A4Poster {...p}/>}
              {tmpl.id === 'counter-card'    && <CounterCard {...p}/>}
              {tmpl.id === 'window-sticker'  && <WindowSticker {...p}/>}
              {tmpl.id === 'door-sticker'    && <DoorSticker {...p}/>}
              {tmpl.id === 'table-tent'      && (
                <div style={{display:'flex',flexDirection:'column'}}>
                  <TableTentFront {...p}/>
                  <TableTentBack biz={bizName}/>
                </div>
              )}
              {tmpl.id === 'menu-insert'     && <MenuInsert {...p}/>}
              {tmpl.id === 'receipt-sticker' && <ReceiptSticker {...p}/>}
              {tmpl.id === 'social-media'    && <SocialTemplate {...p}/>}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="kit-no-print" data-testid="marketing-kit-footer" style={{
        background:C.charcoal, borderTop:`3.5px solid ${C.gold}`,
        padding:'14px 24px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8,
      }}>
        <p style={{fontFamily:F.sans,fontSize:11,color:`${C.cream}45`,margin:0}}>Perkly · {bizName} · useperkly.com</p>
        <p style={{fontFamily:F.sans,fontSize:11,color:`${C.gold}80`,margin:0,letterSpacing:'0.06em'}}>Discover Athens. Save Money.</p>
      </div>
    </>
  );
}
